import { readPdfPageRotation } from "./pdfOrientation.mjs";
import { pdfRotation } from "./pdfViewPreferences.mjs";

/** Print rendered pages locally; never submit a job to Work/Sage printers. */
export async function printPdf(pdf, { api, document = window.document, signal, identity, onStatus = () => {} } = {}) {
    const frame = document.createElement("iframe"), urls = [];
    frame.title = "Stampa PDF";
    frame.setAttribute("aria-hidden", "true");
    frame.style.cssText = "position:fixed;width:1px;height:1px;right:0;bottom:0;border:0";
    let timer, released = false, render;
    const check = () => { if (signal?.aborted) throw new DOMException("Stampa annullata", "AbortError"); };
    const release = () => {
        if (released) return;
        released = true;
        signal?.removeEventListener("abort", abort);
        window.clearTimeout(timer);
        frame.remove(); urls.forEach((url) => URL.revokeObjectURL(url));
    };
    const abort = () => { render?.cancel(); release(); };
    try {
        check();
        signal?.addEventListener("abort", abort, { once: true });
        // about:blank inherits the application's origin; no scripts or PDF URLs
        // are injected and filenames never become markup.
        document.body.appendChild(frame);
        const target = frame.contentDocument;
        const style = target.createElement("style");
        style.textContent = "@page{margin:0;size:auto}html,body{margin:0}img{display:block;width:100%;height:100%;object-fit:contain;break-after:page}img:last-child{break-after:auto}";
        target.head.appendChild(style);
        for (let number = 1; number <= pdf.numPages; number++) {
            check();
            onStatus(`Preparazione stampa ${number} / ${pdf.numPages}…`);
            const page = await pdf.getPage(number);
            check();
            const rotation = (page.rotate + pdfRotation(identity || pdf.fingerprints?.[0], await readPdfPageRotation(page), number)) % 360;
            const natural = page.getViewport({ scale: 1, rotation });
            const scale = Math.min(150 / 72, Math.sqrt(8000000 / (natural.width * natural.height)));
            const viewport = page.getViewport({ scale, rotation });
            const canvas = document.createElement("canvas");
            canvas.width = Math.ceil(viewport.width); canvas.height = Math.ceil(viewport.height);
            let blob;
            try {
                render = page.render({ canvasContext: canvas.getContext("2d"), viewport,
                    annotationMode: api.AnnotationMode.ENABLE, background: "rgb(255,255,255)" });
                await render.promise;
                render = null;
                check();
                blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
                if (!blob) throw new Error("Pagina non disponibile per la stampa.");
            } finally { canvas.width = 0; canvas.height = 0; }
            const url = URL.createObjectURL(blob); urls.push(url);
            const image = target.createElement("img");
            image.src = url; image.alt = `Pagina ${number}`;
            const pageName = `pdf-page-${number}`;
            style.textContent += `@page ${pageName}{size:${natural.width}pt ${natural.height}pt;margin:0}`;
            image.style.page = pageName;
            image.style.width = `${natural.width / 72}in`; image.style.height = `${natural.height / 72}in`;
            target.body.appendChild(image);
            await image.decode();
            check();
        }
        frame.contentWindow.addEventListener("afterprint", release, { once: true });
        timer = window.setTimeout(release, 300000);
        frame.contentWindow.focus();
        frame.contentWindow.print();
    } catch (error) { release(); throw error; }
    return release;
}
